import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const STATUS_STYLES = {
  'In Progress': { background:'#fef9c3', color:'#854d0e' },
  'Issued':      { background:'#dcfce7', color:'#166534' },
};

export default function InvoicesPage() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    api.get('/billing/invoices')
      .then(({ data: res }) => setData(res))
      .catch(err => setError(err.response?.data?.error || 'Failed to load invoices'))
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = async (period, periodLabel) => {
    setDownloading(period);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const resp = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/resources/invoice-download?period=${period}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error('Download failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${period}-ishan-gcp.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Download failed: ' + err.message);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div style={s.layout}>
      <Sidebar />
      <main style={s.main}>

        <div style={s.header}>
          <div>
            <div style={s.breadcrumb}>Billing</div>
            <h1 style={s.pageTitle}>Invoices & Documents</h1>
            <p style={s.pageSubtitle}>Billing statements from your GCP account
              {data?.billingAccount?.displayName && <code style={s.accountTag}> {data.billingAccount.displayName}</code>}
            </p>
          </div>
          {data?.consoleUrl && (
            <a href={data.consoleUrl} target="_blank" rel="noreferrer" style={s.consoleBtn}>
              Open GCP Console →
            </a>
          )}
        </div>

        {error && <div style={s.errorBox}>⚠ {error}</div>}
        {loading && <div style={s.loadingWrap}><div style={s.spinner}/><span style={{color:'#94a3b8'}}>Fetching billing data...</span></div>}

        {data && (
          <>
            {/* Account bar */}
            <div style={s.accountBar}>
              {[
                { label:'Account Name', value: data.billingAccount?.displayName||'—' },
                { label:'Account ID', value: data.billingAccount?.id||'—', mono:true },
                { label:'Status', value: data.billingAccount?.open?'● Active':'○ Closed', color: data.billingAccount?.open?'#16a34a':'#dc2626' },
                { label:'Currency', value: data.billingAccount?.currencyCode||'USD' },
                { label:'Linked Projects', value: data.projects?.length||0 },
              ].map((item,i) => (
                <React.Fragment key={i}>
                  {i>0 && <div style={s.divider}/>}
                  <div style={s.accountItem}>
                    <span style={s.accountLabel}>{item.label}</span>
                    <span style={{ ...s.accountValue, color:item.color||'#0f172a', fontFamily:item.mono?'monospace':'inherit', fontSize:item.mono?12:13 }}>{item.value}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>

            {!isAdmin && (
              <div style={s.infoBox}>
                🔒 <strong>Summary View —</strong> Contact your administrator for full invoice access and PDF downloads.
              </div>
            )}

            {/* Invoice table with download */}
            <div style={s.tableCard}>
              <div style={s.tableHeader}>
                <h3 style={s.chartTitle}>Billing Periods</h3>
                <span style={s.badge}>{data.invoices?.length} months</span>
              </div>
              <table style={s.table}>
                <thead>
                  <tr style={{ background:'#f8fafc' }}>
                    {['Billing Period','Projects Billed','Currency','Status', isAdmin?'Download':'Info'].map(h => (
                      <th key={h} style={s.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(data.invoices||[]).map((inv,i) => (
                    <tr key={i} onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'} onMouseLeave={e=>e.currentTarget.style.background='#fff'} style={{background:'#fff'}}>
                      <td style={s.td}>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <span style={{ fontSize:20 }}>{i===0?'📋':'🧾'}</span>
                          <div>
                            <div style={{ color:'#0f172a', fontWeight:600, fontSize:14 }}>{inv.period}</div>
                            <div style={{ color:'#94a3b8', fontSize:11 }}>{inv.periodKey}</div>
                          </div>
                        </div>
                      </td>
                      <td style={s.td}><span style={{ color:'#64748b' }}>{inv.projectCount} project{inv.projectCount!==1?'s':''}</span></td>
                      <td style={s.td}><span style={{ color:'#64748b' }}>{data.billingAccount?.currencyCode||'USD'}</span></td>
                      <td style={s.td}>
                        <span style={{ ...(STATUS_STYLES[inv.status]||STATUS_STYLES['Issued']), padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600 }}>
                          {inv.status}
                        </span>
                      </td>
                      <td style={s.td}>
                        {isAdmin ? (
                          <div style={{ display:'flex', gap:8 }}>
                            <button
                              onClick={() => handleDownload(inv.periodKey, inv.period)}
                              disabled={!!downloading}
                              style={{
                                ...s.downloadBtn,
                                opacity: downloading===inv.periodKey ? 0.6 : 1,
                                background: downloading===inv.periodKey ? '#f1f5f9' : '#eff6ff',
                                cursor: downloading ? 'wait' : 'pointer',
                              }}
                            >
                              {downloading===inv.periodKey ? '⏳ Generating...' : '⬇ Download'}
                            </button>
                            <a href={inv.downloadUrl} target="_blank" rel="noreferrer" style={s.gcpLink}>GCP Console ↗</a>
                          </div>
                        ) : (
                          <span style={{ color:'#94a3b8', fontSize:12 }}>Contact admin</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Linked projects */}
            <div style={s.tableCard}>
              <div style={s.tableHeader}>
                <h3 style={s.chartTitle}>Billed Projects</h3>
                <span style={s.badge}>{data.projects?.length} total</span>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:8, padding:'4px 0' }}>
                {(data.projects||[]).map((p,i) => (
                  <div key={i} style={s.projectRow}>
                    <span style={{ fontSize:18 }}>📂</span>
                    <div style={{ flex:1 }}>
                      <code style={s.code}>{p.projectId}</code>
                      <div style={{ color:'#94a3b8', fontSize:11, marginTop:2 }}>{p.billingAccountName?.replace('billingAccounts/','Account: ')||'—'}</div>
                    </div>
                    <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600, background:p.billingEnabled?'#dcfce7':'#fee2e2', color:p.billingEnabled?'#166534':'#991b1b' }}>
                      {p.billingEnabled?'● Billing On':'○ Billing Off'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Note */}
            <div style={s.noteCard}>
              <span style={{ fontSize:20 }}>💡</span>
              <div style={{ fontSize:13, color:'#64748b', lineHeight:1.7 }}>
                <strong style={{ color:'#1d4ed8' }}>Downloaded invoices</strong> are HTML documents generated from live GCP Billing API data and open directly in any browser.
                For official PDF invoices, visit{' '}
                <a href={data.consoleUrl} target="_blank" rel="noreferrer" style={{ color:'#2563eb' }}>GCP Console → Billing → Documents & Statements</a>.
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

const s = {
  layout:{display:'flex',background:'#f5f7fa',minHeight:'100vh',fontFamily:"'Inter','Segoe UI',sans-serif"},
  main:{marginLeft:248,flex:1,padding:'28px 32px'},
  header:{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20},
  breadcrumb:{color:'#94a3b8',fontSize:12,fontWeight:500,marginBottom:4},
  pageTitle:{margin:0,color:'#0f172a',fontSize:24,fontWeight:700,letterSpacing:'-0.3px'},
  pageSubtitle:{margin:'4px 0 0',color:'#64748b',fontSize:13},
  accountTag:{background:'#f1f5f9',padding:'2px 8px',borderRadius:6,fontFamily:'monospace',fontSize:12,color:'#475569'},
  consoleBtn:{background:'linear-gradient(135deg,#1d4ed8,#2563eb)',color:'#fff',borderRadius:8,padding:'9px 18px',fontSize:13,fontWeight:600,textDecoration:'none',display:'inline-block'},
  errorBox:{background:'#fef2f2',border:'1px solid #fecaca',borderRadius:10,padding:'12px 16px',color:'#dc2626',fontSize:14,marginBottom:20},
  loadingWrap:{display:'flex',alignItems:'center',gap:14,padding:60,justifyContent:'center'},
  spinner:{width:28,height:28,border:'3px solid #bfdbfe',borderTopColor:'#2563eb',borderRadius:'50%',animation:'spin 0.8s linear infinite'},
  accountBar:{display:'flex',alignItems:'center',background:'#fff',border:'1px solid #e2e8f0',borderRadius:12,padding:'14px 22px',marginBottom:20,boxShadow:'0 1px 4px rgba(0,0,0,0.04)',gap:0},
  accountItem:{display:'flex',flexDirection:'column',gap:4,padding:'0 20px'},
  accountLabel:{color:'#94a3b8',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.8px'},
  accountValue:{fontWeight:600,fontSize:13},
  divider:{width:1,height:32,background:'#f1f5f9'},
  infoBox:{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:10,padding:'12px 16px',color:'#1d4ed8',fontSize:13,marginBottom:20},
  tableCard:{background:'#fff',border:'1px solid #e2e8f0',borderRadius:12,overflow:'hidden',marginBottom:16,boxShadow:'0 1px 4px rgba(0,0,0,0.04)'},
  tableHeader:{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'16px 20px',borderBottom:'1px solid #f1f5f9'},
  chartTitle:{margin:0,color:'#0f172a',fontSize:15,fontWeight:600},
  badge:{background:'#f1f5f9',color:'#64748b',fontSize:11,padding:'4px 10px',borderRadius:20},
  table:{width:'100%',borderCollapse:'collapse'},
  th:{textAlign:'left',color:'#64748b',fontSize:11,fontWeight:600,letterSpacing:'0.5px',textTransform:'uppercase',padding:'10px 20px',borderBottom:'1px solid #f1f5f9'},
  td:{padding:'14px 20px',color:'#374151',fontSize:13,borderBottom:'1px solid #f8fafc',transition:'background 0.1s'},
  downloadBtn:{border:'1.5px solid #bfdbfe',color:'#1d4ed8',borderRadius:7,padding:'6px 14px',fontSize:12,fontWeight:600,cursor:'pointer',transition:'all 0.15s'},
  gcpLink:{color:'#64748b',fontSize:12,textDecoration:'none',padding:'6px 10px',display:'inline-flex',alignItems:'center'},
  projectRow:{display:'flex',alignItems:'center',gap:12,padding:'12px 20px',borderBottom:'1px solid #f8fafc'},
  code:{background:'#f1f5f9',padding:'2px 8px',borderRadius:5,fontFamily:'monospace',fontSize:13,color:'#475569',fontWeight:600},
  noteCard:{display:'flex',gap:14,background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:10,padding:'14px 16px',alignItems:'flex-start'},
};
